import React, { useRef, useState } from 'react';
import { useCms } from '@/context/CmsContext';
import { uploadMedia } from '@/services/cms';
import styles from './cms.module.css';

interface ImageUploaderProps {
    page: string;
    section: string;
    field: string;
    currentUrl: string;
    onSave?: (newUrl: string) => void;
    children: React.ReactNode;
}

export default function ImageUploader({ page, section, field, currentUrl, onSave, children }: ImageUploaderProps) {
    const { isAdmin, isEditMode, trackChange, getPendingValue } = useCms();
    const [showModal, setShowModal] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const [urlInput, setUrlInput] = useState(currentUrl);
    const fileRef = useRef<HTMLInputElement>(null);

    const pendingValue = getPendingValue(page, section, field);
    const hasChange = pendingValue !== undefined;

    if (!isAdmin || !isEditMode) {
        return <>{children}</>;
    }

    const handleUpload = async (file: File) => {
        setUploading(true);
        setUploadError('');
        try {
            const media = await uploadMedia(file);
            trackChange(page, section, field, media.r2_key, currentUrl, 'image');
            onSave?.(media.r2_key);
            setShowModal(false);
        } catch (err) {
            setUploadError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
        } finally {
            setUploading(false);
        }
    };

    const handleUrlSave = () => {
        trackChange(page, section, field, urlInput, currentUrl, 'image');
        onSave?.(urlInput);
        setShowModal(false);
    };

    const wrapperClass = hasChange
        ? `${styles.editableImage} ${styles.editableImageChanged}`
        : styles.editableImage;

    return (
        <>
            <div
                className={wrapperClass}
                onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setUrlInput(pendingValue || currentUrl);
                    setShowModal(true);
                }}
                title={`Click to change image: ${field}`}
            >
                {children}
                <div className={styles.imageOverlay}>
                    <span className={styles.imageOverlayText}>
                        {hasChange ? 'Changed — click to update' : 'Click to change image'}
                    </span>
                </div>
            </div>

            {showModal && (
                <div className={styles.modal} onClick={() => setShowModal(false)}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <h3 className={styles.modalTitle}>Update Image</h3>

                        <div className={styles.modalSection}>
                            <label>Upload file</label>
                            <input
                                ref={fileRef}
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) handleUpload(file);
                                }}
                                disabled={uploading}
                            />
                        </div>

                        <div className={styles.modalDivider}>or</div>

                        <div className={styles.modalSection}>
                            <label>Image URL</label>
                            <input
                                type="text"
                                value={urlInput}
                                onChange={(e) => setUrlInput(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleUrlSave()}
                                className={styles.editInput}
                                style={{ width: '100%' }}
                                placeholder="https://..."
                            />
                        </div>

                        {uploading && <div className={styles.savingIndicator}>Uploading...</div>}
                        {uploadError && <div style={{ color: '#ef4444', fontSize: 13, marginTop: 8 }}>{uploadError}</div>}

                        <div className={styles.modalActions}>
                            <button onClick={handleUrlSave} className={styles.saveButton} disabled={uploading}>
                                Apply
                            </button>
                            <button onClick={() => setShowModal(false)} className={styles.cancelButton}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
